import React, {useState} from 'react';

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  MutationDefinition,
} from '@reduxjs/toolkit/dist/query';
import {UseMutation} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import {capitalizeFirstLetter} from 'js-libs/sweetcommons/strings';

import {TaskActionDocumentPopover} from 'app/components/general/TaskActionDocumentPopover';
import {AttachOverlay} from 'app/general/overlays';
import {DateRangePopover} from 'app/general/widgets/DateRangePopover';
import {gt} from 'app/utils/i18n';

import type {DateRangeType} from 'app/general/dates/date-range';
import type {ContentType} from 'sweet-types';

interface paramType {
  contentType: string;
  objectId: number | string;
  range: DateRangeType;
}

interface ResponseType {
  result: string;
}

type UseExportHookType = UseMutation<
  MutationDefinition<
    paramType,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    string,
    ResponseType,
    'sweetAPI'
  >
>;

interface DateRangeExportButtonType {
  itemsType: string;
  className: string;
  objectId: number | string;
  contentType: ContentType;
  successMessage: string;
  useExportHook: UseExportHookType;
}

export const DateRangeExportButton = ({
  objectId,
  itemsType,
  className,
  contentType,
  successMessage,
  useExportHook,
}: DateRangeExportButtonType) => {
  const [exportData] = useExportHook();
  const [status, setStatus] = useState<'idle' | 'exporting'>('idle');
  const [dateRange, setDateRange] = useState<DateRangeType>({
    start: '',
    end: '',
  });
  const [canMount, setCanMount] = useState<boolean>(false);
  const capitalizedItemType = capitalizeFirstLetter(itemsType);
  const title = gt('Export %(item)s', {item: capitalizedItemType});

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) =>
        canMount ? (
          status === 'idle' ? (
            <DateRangePopover
              title={title}
              setStatus={setStatus}
              overlayProps={overlayProps}
              setDateRange={setDateRange}
            />
          ) : (
            <TaskActionDocumentPopover
              contentType={contentType}
              overlayProps={{
                ...overlayProps,
                placement: 'top',
                toggle: () => {
                  setStatus('idle');
                  overlayProps.toggle();
                },
              }}
              objType={itemsType}
              title={title}
              verb={gt('Export')}
              gerundVerb={gt('exporting')}
              successMessage={successMessage}
              action={() =>
                exportData({
                  objectId,
                  contentType,
                  range: dateRange,
                }).unwrap()
              }
              downloadLink={true}
            />
          )
        ) : null
      }
    >
      {(handlers) => (
        <button
          className={className}
          {...handlers}
          onClick={() => {
            if (handlers.onClick) handlers.onClick();
            setCanMount(true);
          }}
        >
          <i className="fa fa-download" />{' '}
          {gt('Export %(item)s', {item: capitalizedItemType})}
        </button>
      )}
    </AttachOverlay>
  );
};
